import React, { useState, useEffect } from "react";
import EmailImageUpload from "../EmailImageUpload";
import { useEmailSections } from "../../Context/EmailSectionContext";

const Header = (props) => {
  // Extract props that would be available in both normal and preview mode
  const { id, previewMode, styleSettings = {}, data } = props;
  
  // Get context sections
  const { sections, updateSection } = useEmailSections();
   // Use context in edit mode, props in preview mode
   const sectionData = previewMode
   ? data || props
   : sections.find(section => section.id === id) || {};
  
  // Extract data with fallbacks (works in both modes)
  const { 
    title = "", 
    description = "", 
    image = null, 
    links = [],
    component_name = "Header"
  } = sectionData;
  
  // Local state for editing
  const [isEditing, setIsEditing] = useState(false);
  const [headerTitle, setHeaderTitle] = useState(title);
  const [headerDescription, setHeaderDescription] = useState(description);
  const [headerImage, setHeaderImage] = useState(image);
  const [navLinks, setNavLinks] = useState(links || []);
  
  // Local state for style fields
  const sectionStyleSettings = sectionData?.styleSettings || {};
  const initialFontFamily = sectionStyleSettings.fontFamily || styleSettings?.fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif";
  const initialPrimaryColor = sectionStyleSettings.primaryColor || styleSettings?.primaryColor || "#333333";
  const initialSecondaryColor = sectionStyleSettings.secondaryColor || styleSettings?.secondaryColor || "#a1894c";
  const initialBackgroundColor = sectionStyleSettings.backgroundColor || styleSettings?.backgroundColor || "#ffffff";
  
  const [headerFontFamily, setHeaderFontFamily] = useState(initialFontFamily);
  const [headerPrimaryColor, setHeaderPrimaryColor] = useState(initialPrimaryColor);
  const [headerSecondaryColor, setHeaderSecondaryColor] = useState(initialSecondaryColor);
  const [headerBackgroundColor, setHeaderBackgroundColor] = useState(initialBackgroundColor);
  
  // Update local state when data changes
   // Sync local state with context/props
  
  useEffect(() => {
    setHeaderTitle(title);
    setHeaderDescription(description);
    setHeaderImage(image);
    setNavLinks(Array.isArray(links) ? links : []);
    
    // Sync style settings from section data or parent
    const sectionStyles = sectionData?.styleSettings || {};
    setHeaderFontFamily(sectionStyles.fontFamily || styleSettings?.fontFamily || "helvetica, 'helvetica neue', arial, verdana, sans-serif");
    setHeaderPrimaryColor(sectionStyles.primaryColor || styleSettings?.primaryColor || "#333333");
    setHeaderSecondaryColor(sectionStyles.secondaryColor || styleSettings?.secondaryColor || "#a1894c");
    setHeaderBackgroundColor(sectionStyles.backgroundColor || styleSettings?.backgroundColor || "#ffffff");
  }, [id, sectionData, styleSettings]);
 
  // Extract styling values with defaults - merge section-specific with parent styles
  const effectiveStyleSettings = {
    ...styleSettings,  // Parent styles (defaults)
    ...sectionStyleSettings  // Section-specific overrides
  };
  const { 
    primaryColor = "#333333", 
    secondaryColor = "#a1894c", 
    fontFamily = "helvetica, 'helvetica neue', arial, verdana, sans-serif",
    backgroundColor = "#ffffff"
  } = effectiveStyleSettings;
  
  // Real-time style update handler
  const handleStyleChange = (styleType, value) => {
    // Update local state immediately for UI responsiveness
    if (styleType === 'fontFamily') {
      setHeaderFontFamily(value);
    } else if (styleType === 'primaryColor') {
      setHeaderPrimaryColor(value);
    } else if (styleType === 'secondaryColor') {
      setHeaderSecondaryColor(value);
    } else if (styleType === 'backgroundColor') {
      setHeaderBackgroundColor(value);
    }
    
    // Get current section data to preserve other fields
    const currentSection = sections.find(section => section.id === id) || {};
    const currentStyleSettings = currentSection.styleSettings || {};
    
    // Map styleType to property name
    const stylePropertyMap = {
      'fontFamily': 'fontFamily',
      'primaryColor': 'primaryColor',
      'secondaryColor': 'secondaryColor',
      'backgroundColor': 'backgroundColor'
    };
    
    // Update the specific style property
    const updatedStyleSettings = {
      ...currentStyleSettings,
      [stylePropertyMap[styleType]]: value
    };
    
    // Save immediately to section data
    updateSection(id, {
      ...currentSection,
      id,
      component_name: currentSection.component_name || component_name,
      styleSettings: updatedStyleSettings
    });
  };
  
  // Save changes to context (only saves the header section, not the whole email)
  const handleSave = () => {
    const currentSection = sections.find(section => section.id === id) || {};
    updateSection(id, {
      id,
      component_name,
      title: headerTitle,
      description: headerDescription,
      image: headerImage,
      links: navLinks,
      styleSettings: {
        fontFamily: headerFontFamily,
        primaryColor: headerPrimaryColor,
        secondaryColor: headerSecondaryColor,
        backgroundColor: headerBackgroundColor,
        ...(currentSection.styleSettings || {}) // Preserve any other style settings
      }
    });
    setIsEditing(false);
  };
  
  // Handle image upload
  const handleImageUpdate = (newImage) => {
    console.log("Header image updated:", newImage);
    
    if (newImage instanceof File) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const imageDataUrl = reader.result;
        setHeaderImage(imageDataUrl);
        const currentSection = sections.find(section => section.id === id) || {};
        updateSection(id, {
          id,
          component_name,
          title: headerTitle,
          description: headerDescription,
          image: imageDataUrl,
          links: navLinks,
          styleSettings: {
            fontFamily: headerFontFamily,
            primaryColor: headerPrimaryColor,
            secondaryColor: headerSecondaryColor,
            backgroundColor: headerBackgroundColor,
            ...(currentSection.styleSettings || {}) // Preserve any other style settings
          }
        });
      };
      reader.onerror = () => {
        console.error("Error reading image file");
      };
      reader.readAsDataURL(newImage);
    } else {
      setHeaderImage(newImage);
    }
  };
  
  // Link management functions
  const addLink = () => {
    setNavLinks([...navLinks, { text: "New Link", url: "#" }]);
  };
  
  const updateLink = (index, field, value) => {
    const newLinks = [...navLinks];
    newLinks[index] = { ...newLinks[index], [field]: value };
    setNavLinks(newLinks);
  };
  
  const removeLink = (index) => {
    setNavLinks(navLinks.filter((_, i) => i !== index));
  };
  
  // Helper function to ensure image URLs are properly formed
  const getSafeImageUrl = (url) => {
    if (!url) return null;
    if (url.includes('&t=')) {
      return url.split('&t=')[0]; // Remove any existing timestamp
    }
    return url;
  };
  
  // EDIT MODE UI
  if (isEditing && !previewMode) {
    return (
      <div className="email-component email-header-editor p-3 mb-3 border rounded">
        <div className="component-editor">
          <h4 className="editor-title mb-3">Edit Header</h4>
          
          <div className="mb-3">
            <label className="form-label fw-bold">Header Title</label>
            <input
              type="text"
              className="form-control"
              value={headerTitle}
              onChange={(e) => setHeaderTitle(e.target.value)}
            />
          </div>
          
          <div className="mb-3">
            <label className="form-label fw-bold">Header Subtitle</label>
            <input
              type="text"
              className="form-control"
              value={headerDescription}
              onChange={(e) => setHeaderDescription(e.target.value)}
            />
          </div>
          
          <div className="mb-3">
            <label className="form-label fw-bold">Logo/Header Image</label>
            <EmailImageUpload
              initialImage={headerImage}
              onImageUpload={handleImageUpdate}
              className="header-image-upload"
            />
          </div>
          
          <div className="mb-3">
            <label className="form-label fw-bold d-flex justify-content-between align-items-center">
              Navigation Links
              <button 
                className="btn btn-sm btn-outline-primary" 
                onClick={addLink}
                type="button"
              >
                + Add Link
              </button>
            </label>
            
            {navLinks.map((link, index) => (
              <div key={index} className="d-flex mb-2 align-items-center">
                <input
                  type="text"
                  className="form-control me-2"
                  placeholder="Link Text"
                  value={link.text}
                  onChange={(e) => updateLink(index, 'text', e.target.value)}
                />
                <input
                  type="text"
                  className="form-control me-2"
                  placeholder="URL"
                  value={link.url}
                  onChange={(e) => updateLink(index, 'url', e.target.value)}
                />
                <button 
                  className="btn btn-sm btn-outline-danger" 
                  onClick={() => removeLink(index)}
                  type="button"
                >
                  &times;
                </button>
              </div>
            ))}
          </div>
          
          <div className="d-flex justify-content-end mt-3">
            <button
              className="btn btn-outline-primary me-2"
              onClick={() => setIsEditing(false)}
              type="button"
            >
              Cancel
            </button>
            <button
              className="btn bgprimary rounded"
              onClick={handleSave}
              type="button"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // VIEW MODE UI
  return (
  <>
      {!previewMode && (
        <div className="email-component email-header mb-3">
        <div className="component-controls mb-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <div className="d-flex align-items-center gap-1">
              <label className="form-label mb-0 me-1" style={{ fontSize: '12px', fontWeight: '500' }}>Font:</label>
              <select
                className="form-select form-select-sm"
                value={headerFontFamily}
                onChange={(e) => handleStyleChange('fontFamily', e.target.value)}
                style={{ width: '120px', fontSize: '12px' }}
              >
                <option value="helvetica, 'helvetica neue', arial, verdana, sans-serif">Helvetica</option>
                <option value="arial, sans-serif">Arial</option>
                <option value="georgia, times, serif">Georgia</option>
                <option value="verdana, geneva, sans-serif">Verdana</option>
                <option value="tahoma, geneva, sans-serif">Tahoma</option>
                <option value="trebuchet ms, helvetica, sans-serif">Trebuchet MS</option>
                <option value="times new roman, times, serif">Times New Roman</option>
              </select>
            </div>
            
            <div className="d-flex align-items-center gap-1">
              <label className="form-label mb-0 me-1" style={{ fontSize: '12px', fontWeight: '500' }}>Primary:</label>
              <input
                type="color"
                className="form-control form-control-color form-control-color-sm"
                value={headerPrimaryColor}
                onChange={(e) => handleStyleChange('primaryColor', e.target.value)}
                style={{ width: '30px', height: '30px', padding: '2px' }}
                title="Primary Color"
              />
            </div>
            
            <div className="d-flex align-items-center gap-1">
              <label className="form-label mb-0 me-1" style={{ fontSize: '12px', fontWeight: '500' }}>Secondary:</label>
              <input
                type="color"
                className="form-control form-control-color form-control-color-sm"
                value={headerSecondaryColor}
                onChange={(e) => handleStyleChange('secondaryColor', e.target.value)}
                style={{ width: '30px', height: '30px', padding: '2px' }}
                title="Secondary Color"
              />
            </div>
            
            <div className="d-flex align-items-center gap-1">
              <label className="form-label mb-0 me-1" style={{ fontSize: '12px', fontWeight: '500' }}>BG:</label>
              <input
                type="color"
                className="form-control form-control-color form-control-color-sm"
                value={headerBackgroundColor}
                onChange={(e) => handleStyleChange('backgroundColor', e.target.value)}
                style={{ width: '30px', height: '30px', padding: '2px' }}
                title="Background Color"
              />
            </div>
          </div>
          
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => setIsEditing(true)}
            type="button"
          >
            Edit Header
          </button>
        </div>
        </div>
      )}
      
      {/* Actual header content */}
     <table width="100%" cellpadding="0" cellspacing="0" border="0" style={{ fontFamily, backgroundColor }}>
     <tr>
          <td style={{ paddingBottom: '16px', backgroundColor }}>
            <table width="100%" cellpadding="0" cellspacing="0" border="0">
              <tr>
                <td align="center" valign="middle">
                  {headerImage && (
                    <img 
                      src={headerImage}
                      alt="Logo" 
                      style={{ maxHeight: '60px', maxWidth: '200px', height: 'auto', display: 'block', margin: '0 auto' }}
                      onError={(e) => {
                        console.error("Header image failed to load:", headerImage);
                        e.target.onerror = null; 
                        e.target.src = "https://via.placeholder.com/200x60/e0e0e0/666666?text=Logo+Error";
                      }}
                    />
                  )}
                </td>
              </tr>
              {navLinks && navLinks.length > 0 && (
                <tr>
                  <td align="center" valign="middle" style={{ paddingTop: '12px' }}>
                    <table cellpadding="0" cellspacing="0" border="0">
                      <tr>
                        {navLinks.map((link, index) => (
                          <td key={index} style={{ padding: '0 8px' }}>
                            <a 
                              href={link.url} 
                              style={{ 
                                color: primaryColor, 
                                textDecoration: 'none', 
                                fontFamily,
                                fontSize: '14px'
                              }}
                            >
                              {link.text}
                            </a>
                          </td>
                        ))}
                      </tr>
                    </table>
                  </td>
                </tr>
              )}
            </table>
          </td>
        </tr>

        {(headerTitle || headerDescription) && (
          <tr>
            <td align="center" style={{ padding: '16px 0', backgroundColor }}>
              {headerTitle && (
                <div 
                  style={{ 
                    color: secondaryColor, 
                    fontWeight: '600', 
                    fontSize: '28px', 
                    fontFamily,
                    marginBottom: '8px'
                  }}
                >
                  {headerTitle}
                </div>
              )}
              {headerDescription && (
                <div 
                  style={{ 
                    color: primaryColor, 
                    fontSize: '16px', 
                    opacity: 0.9, 
                    fontFamily
                  }}
                >
                  {headerDescription}
                </div>
              )}
            </td>
          </tr>
        )}
</table>

</>
  )
};

export default Header; 